import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { FormSectionProps } from "@/components/controls/Form/FormSection";

const NONE_VALUE = "none";

// Stable empty ref so callers that omit seededValues don't recreate handleCloseAttempt each render
const EMPTY_SEEDED_VALUES: Record<string, unknown> = {};

// Build a map of field names to their default values from form sections
const buildDefaultValuesMap = (
  formSections?: FormSectionProps[]
): Record<string, any> => {
  if (!formSections) return {};

  const defaultValues: Record<string, any> = {};

  formSections.forEach((section) => {
    section.fields?.forEach((field) => {
      if (field.name && field.defaultValue !== undefined) {
        defaultValues[field.name] = field.defaultValue;
      }
    });
  });

  return defaultValues;
};

// checks if any field in the form has a non-empty value that differs from its default
const hasAnyFieldValue = (
  formData: Record<string, any>,
  defaultValues: Record<string, any>
): boolean => {
  if (!formData || Object.keys(formData).length === 0) {
    return false;
  }

  return Object.entries(formData).some(([fieldName, value]) => {
    const defaultValue = defaultValues[fieldName];

    // If value matches the default value, consider it unchanged
    if (defaultValue !== undefined && value === defaultValue) {
      return false;
    }

    // Check for non-empty strings (excluding "none")
    if (
      typeof value === "string" &&
      value.trim().length > 0 &&
      value !== NONE_VALUE
    ) {
      return true;
    }
    if (typeof value === "number" && value > 0) return true;
    if (Array.isArray(value) && value.length > 0) {
      const nonNoneValues = value.filter((item) => item !== NONE_VALUE);
      return nonNoneValues.length > 0;
    }

    if (value && typeof value === "object" && !Array.isArray(value)) {
      return Object.keys(value).length > 0;
    }

    return false;
  });
};

export interface UnsavedChangesWarningDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  discardButtonText: string;
  cancelButtonText: string;
  onDiscard: () => void;
  onCancel: () => void;
}

export interface UseUnsavedChangesReturn {
  handleCloseAttempt: (onConfirm: () => void) => void;
  warningDialogProps: UnsavedChangesWarningDialogProps;
}

export const useUnsavedChanges = (
  getCurrentFormValues: () => Record<string, any>,
  formSections?: FormSectionProps[],
  // Values the caller auto-populates on open (e.g. AE Owner = logged-in user).
  // Treated as defaults so a system pre-fill isn't mistaken for a user edit.
  // Must be referentially stable (module const or useState/useRef) — an unstable
  // object recreates handleCloseAttempt on every render.
  seededValues: Record<string, unknown> = EMPTY_SEEDED_VALUES
): UseUnsavedChangesReturn => {
  const { t } = useTranslation();

  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  const handleCloseAttempt = useCallback(
    (onConfirm: () => void) => {
      const currentValues = getCurrentFormValues();
      const defaultValues = {
        ...buildDefaultValuesMap(formSections),
        ...seededValues
      };
      const hasChanges = hasAnyFieldValue(currentValues, defaultValues);

      if (hasChanges) {
        setPendingAction(() => onConfirm);
        setIsWarningOpen(true);
      } else {
        // No changes, close immediately
        onConfirm();
      }
    },
    [getCurrentFormValues, formSections, seededValues]
  );

  const closeWarning = useCallback(() => {
    setIsWarningOpen(false);
    setPendingAction(null);
  }, []);

  const confirmDiscard = useCallback(() => {
    if (pendingAction) {
      pendingAction();
    }
    setIsWarningOpen(false);
    setPendingAction(null);
  }, [pendingAction]);

  const warningDialogProps: UnsavedChangesWarningDialogProps = {
    isOpen: isWarningOpen,
    title: t("unsavedChanges.title"),
    message: t("unsavedChanges.message"),
    discardButtonText: t("unsavedChanges.discard"),
    cancelButtonText: t("cancel"),
    onDiscard: confirmDiscard,
    onCancel: closeWarning
  };

  return {
    handleCloseAttempt,
    warningDialogProps
  };
};
